import React, { useEffect, useState, useRef } from "react";
import config from "config.json";
import useFetch from "hooks/useFetch";

interface Ocupante {
  id: number;
  dni: string;
  apellido_nombre: string;
  condicion: string;
  fecha_nacimiento: string;
}

interface Habitacion {
  cantidad_ocupantes: number;
  codigo_habitacion: string;
  codigo_categoria: string;
  descripcion_categoria: string;
  ocupantes: Ocupante[];
}

interface ReservaDatos {
  id: number;
  fecha_inicio: string;
  fecha_fin: string;
  estado: string;
  datos_completos_reserva: {
    habitaciones: Habitacion[];
    codigo_hotel: string;
    fecha_inicio: string;
    fecha_fin: string;
  };
  nombre_hotel: string;
}

const CheckInPage: React.FC = () => {
  const type = new URLSearchParams(window.location.search).get("type"); // 'in' o 'out'
  const id = new URLSearchParams(window.location.search).get("id");
  const [pin, setPin] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [error, setError] = useState<boolean | null>(null);
  const [checkInSuccess, setCheckInSuccess] = useState<boolean | null>(null); // Estado para controlar el resultado
  const [fechaInicioReserva, setFechaInicioReserva] = useState<Date>()
  const [diaDespues, setDiaDespues] = useState<Date>()

  // Ref para asegurar que solo llamemos a la API una vez
  const hasFetchedData = useRef(false);

  const { data: reservationData, error: reservationError, isLoading: reservationIsLoading, fetchData: reservationFetchData } = useFetch<ReservaDatos>(
    "GET",
    `${config.backend_url}/setia/reservas/uuid?uuid=${id}`,
    {},
    { "Content-Type": "application/json" }
  );

  const { data: confirmationData, error: confirmationError, isLoading: confirmationIsLoading, fetchData: confirmationFetchData } = useFetch(
    "GET",
    `${config.backend_url}/setia/reserva/check?id=${id}&checkin=${type === 'in'}&pin=${pin}`,
    {},
    { "Content-Type": "application/json" }
  );

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return "Fecha no disponible";
    }

    // Dividimos el string en año, mes y día
    const [year, month, day] = dateString.split("-");

    // Array de meses en español
    const meses = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio", 
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];

    // Convertimos el mes a número y ajustamos (el array es 0-indexed)
    const mes = meses[parseInt(month, 10) - 1];

    return `${day} ${mes} ${year}`;
  };

  // Llamada para obtener datos de la reserva solo una vez
  useEffect(() => {
    if (id && !hasFetchedData.current) {
      reservationFetchData();
      hasFetchedData.current = true; // Marcar que ya se hizo el fetch
    }
  }, [id, reservationFetchData]);

  const handleConfirm = async () => {
    confirmationFetchData();
  };

  useEffect(() => {
    // Si la confirmación fue exitosa
    if (confirmationData && !confirmationError) {
      setModalMessage("Confirmación exitosa.");
      setShowModal(true);
      setError(null);
      setCheckInSuccess(true); // Check-in exitoso
    }

    // Si ocurrió un error en la confirmación
    if (confirmationError) {
      setModalMessage("Error al confirmar la reserva.");
      setShowModal(true);
      setError(true);
      setCheckInSuccess(false); // Check-in fallido
    }
  }, [confirmationData, confirmationError]);

  useEffect(() => {
    if (reservationData) {
      setFechaInicioReserva(new Date(reservationData.fecha_inicio +"T03:00:00Z"))
      let despues = new Date(reservationData.fecha_inicio +"T03:00:00Z")
      despues.setDate(despues.getDate() + 1)
      setDiaDespues(despues)
    }
  }, [reservationData])
  
  // if(type === 'in' && fechaInicioReserva && diaDespues && (new Date() < fechaInicioReserva || new Date() > diaDespues ) ) {
  //   return (
  //     <div className="flex justify-center items-center min-h-screen">
  //       <h1 className="text-xl text-red-500">No habilitado, se debe realizar el checkin en el dia de la reserva</h1>
  //     </div>
  //   );
  // }
  if (id == null) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <h1 className="text-xl text-red-500">No se encontró la reserva</h1>
      </div>
    );
  }

  if (reservationIsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div>Cargando datos de la reserva...</div>
      </div>
    );
  }

  if (reservationError) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500">Error al cargar los datos de la reserva.</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
        { type === 'in' ? <h1 className="text-2xl font-bold mb-4 text-center">Check-in</h1> : <h1 className="text-2xl font-bold mb-4 text-center">Check-out</h1> }

      <h1 className="text-2xl font-bold mb-4 text-center">Detalles de la Reserva {reservationData?.id}:</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white shadow-md p-4 rounded-lg">
          <p><strong>Hotel:</strong> {reservationData?.nombre_hotel}</p>
          <p><strong>Desde:</strong> {formatDate(reservationData?.fecha_inicio)}</p>
          <p><strong>Hasta:</strong> {formatDate(reservationData?.fecha_fin)}</p>

          <h2 className="text-xl mt-4 mb-2 font-bold">Ocupantes:</h2>
          {reservationData?.datos_completos_reserva?.habitaciones?.map((habitacion, habitacionIndex) => (
            <div key={habitacionIndex} className="mb-4">
              <h3 className="font-bold text-blue-500 mb-4">Habitación: {habitacion.codigo_habitacion}</h3>
              {habitacion.ocupantes.map((ocupante, ocupanteIndex) => (
                <div key={ocupanteIndex} className="mb-2">
                  <p><strong>Nombre:</strong> {ocupante.apellido_nombre}</p>
                  <p><strong>DNI:</strong> {ocupante.dni}</p>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="bg-white shadow-md p-4 rounded-lg">
          <label htmlFor="pin" className="block text-sm font-medium text-gray-700">Ingresa el PIN de confirmación</label>
          <input
            type="number"
            id="pin"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="mt-2 p-2 border rounded-md w-full"
            placeholder="Ingrese el PIN"
          />

          <button
            onClick={handleConfirm}
            disabled={!pin}
            className={`mt-4 w-full py-2 px-4 rounded-full text-white ${pin ? 'bg-blue-500' : 'bg-gray-400 cursor-not-allowed'}`}
          >
            Confirmar
          </button>
        </div>
      </div>

      {/* Modal nativo con HTML y CSS */}
      {showModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-11/12 md:w-1/3">
            <h2 className="text-xl font-bold mb-4 text-center">{modalMessage}</h2>
            <button
              onClick={() => {
                setShowModal(false);
                if (checkInSuccess) window.location.href = "/"; // Si el check-in fue exitoso, redirigir al home
              }}
              className="w-full py-2 px-4 rounded-full text-white bg-blue-500"
            >
              {error ? "Reintentar" : "Ir a la Home"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInPage;
